import { api, apiGet, apiPost } from './api';
import { broadcast } from './broadcast';
import { useAuthStore, type AuthUser, type SubscriptionInfo } from '@/stores/auth';
import { identifyUser, resetPostHog } from '@/lib/posthog';

export interface AuthResponse {
  user: AuthUser;
  tokens: { accessToken: string };
  subscription?: SubscriptionInfo | null;
  isNewUser?: boolean;
  needsOnboarding?: boolean;
}

export interface RegisterPayload {
  email: string;
  password: string;
  name?: string;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface SocialPayload {
  provider: 'google';
  idToken: string;
}

function applyAuth(res: AuthResponse): AuthResponse {
  useAuthStore.getState().setAuthenticated({
    user: res.user,
    accessToken: res.tokens.accessToken,
    subscription: res.subscription ?? null,
  });
  identifyUser(res.user.id, { email: res.user.email });
  broadcast({ type: 'signin' });
  return res;
}

/**
 * Ask for a reset link. Resolves for any address, existing or not — the API
 * answers 204 either way on purpose, so there is nothing here to branch on.
 * Only a 429 from the per-IP limiter can reject.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiPost<void>('/auth/forgot-password', { email });
}

/** Redeem a reset token. Throws when the link is expired or already used. */
export async function resetPassword(token: string, password: string): Promise<void> {
  await apiPost<void>('/auth/reset-password', { token, password });
}

export async function signIn(payload: LoginPayload): Promise<AuthResponse> {
  const res = await apiPost<AuthResponse>('/auth/login', payload);
  return applyAuth(res);
}

export async function signUp(payload: RegisterPayload): Promise<AuthResponse> {
  const res = await apiPost<AuthResponse>('/auth/register', payload);
  return applyAuth(res);
}

export async function socialSignIn(payload: SocialPayload): Promise<AuthResponse> {
  const res = await apiPost<AuthResponse>('/auth/social', payload);
  return applyAuth(res);
}

/**
 * Cold-start refresh: called once on app boot. The browser sends the
 * httpOnly bz_rt cookie automatically (axios `withCredentials: true`).
 * On success we have a fresh access token and pull /users/me.
 */
export async function refreshSession(): Promise<boolean> {
  try {
    const refresh = await apiPost<{ accessToken: string }>(
      '/auth/refresh',
      {},
      { headers: { 'X-Requested-With': 'fetch' } },
    );
    if (!refresh.accessToken) return false;

    useAuthStore.getState().setAccessToken(refresh.accessToken);

    const me = await apiGet<AuthUser>('/users/me');
    let subscription: SubscriptionInfo | null = null;
    try {
      subscription = await apiGet<SubscriptionInfo | null>('/subscription');
    } catch {
      // /subscription may 404 for users with no row yet — treat as no premium.
      subscription = { isPremium: false, plan: 'free', endDate: null };
    }

    useAuthStore.getState().setAuthenticated({
      user: me,
      accessToken: refresh.accessToken,
      subscription,
    });
    identifyUser(me.id, { email: me.email });
    return true;
  } catch {
    useAuthStore.getState().clear();
    return false;
  }
}

export async function signOut(): Promise<void> {
  try {
    await apiPost('/auth/logout');
  } catch {
    /* swallow — logout is fire-and-forget; the cookie is still cleared client-side via the API response */
  }
  useAuthStore.getState().clear();
  resetPostHog();
  broadcast({ type: 'signout' });
}

/** Fetch the latest /users/me + /subscription — used on visibilitychange. */
export async function refreshUserData(): Promise<void> {
  if (useAuthStore.getState().status !== 'authenticated') return;
  try {
    const [me, subscription] = await Promise.all([
      apiGet<AuthUser>('/users/me'),
      apiGet<SubscriptionInfo | null>('/subscription').catch(() => null),
    ]);
    useAuthStore.getState().setUser(me);
    useAuthStore
      .getState()
      .setSubscription(subscription ?? { isPremium: false, plan: 'free', endDate: null });
  } catch {
    /* network blip or 401 — the interceptor will surface it */
  }
}

// Re-export the axios instance for direct use where needed (e.g. file uploads).
export { api };
